import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye } from "lucide-react";

import { GenericTable } from "@shared/components/GenericTable";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
import { TableAction } from "@shared/types/table";
import { useToast } from "@shared/hooks/use-toast";
import { useValidationContext } from "../../context";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useReadingsList,
  useRoutesListForNavigation,
  useValidationBreakList,
} from "../../hooks";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { missingReadingHeaders } from "../../types";

const PAGE_SIZE = 50;

function NavigateToReading() {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const { toast } = useToast();
  const { setViewMode, setValidatorId } = useValidationContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [accordionOpen, setAccordionOpen] = useState("");

  const tabOptions = [
    { label: "Pending", value: "pending" },
    { label: "Exemptions", value: "exemptions" },
    { label: "Missing Readings", value: "missing-readings" },
    { label: "Revisit", value: "revisit" },
    { label: "Ghost Meters", value: "new-readings" },
    { label: "Pass", value: "pass" },
  ];

  const tabToRuleStatusMap: Record<string, string> = {
    pass: "PASS",
    pending: "NEW",
    exemptions: "FAIL",
    revisit: "REVISIT",
    "new-readings": "GHOST_METER",
    "missing-readings": "MISSING_READING",
  };

  const ruleStatusToTabMap: Record<string, string> = Object.fromEntries(
    Object.entries(tabToRuleStatusMap).map(([tab, status]) => [
      status.toLowerCase(),
      tab,
    ]),
  );

  // ====== Fetch Cycles ======
  const { data: activeCycles } = useValidationBreakList({
    remoteUtilityId,
    page: 1,
    limit: PAGE_SIZE,
  });

  const cyclesDropdownList = activeCycles?.results?.map(
    ({ meterCycleName, createdMonth, runId }) => ({
      label: `${meterCycleName} - ${createdMonth}`,
      value: runId,
    }),
  );

  // ====== Fetch Routes ======
  const { data: routes } = useRoutesListForNavigation(
    { page: 1, limit: PAGE_SIZE, isBaseInfo: true, isPaginationRequired: true },
    remoteUtilityId,
  );

  const routesDropdownList = (routes?.results ?? []).map((route) => ({
    label: route.routeName,
    value: JSON.stringify(route.premise),
  }));

  // ====== Form State ======
  const [formState, setFormState] = useState({
    cycle: [],
    tab: "",
    route: [],
    search: "",
  });

  // ====== Debounce Search ======
  useEffect(() => {
    const delay = setTimeout(() => {
      setFormState((prev) => ({ ...prev, search: searchValue }));
    }, 400);
    return () => clearTimeout(delay);
  }, [searchValue]);

  // ====== Fetch Reading List ======
  const { data: pendingReadingList, isFetching } = useReadingsList({
    remoteUtilityId,
    meterRunId: formState.cycle[0],
    page: currentPage,
    limit: PAGE_SIZE,
    ruleStatus: formState.tab ? tabToRuleStatusMap[formState.tab] : undefined,
    searchData: formState.search,
    premises: formState.route?.length > 0 ? formState.route.flat() : undefined,
  });

  const navigateFields: FormField[] = [
    {
      name: "cycle",
      type: "multi-select",
      maxSelection: 1,
      closeOnSelect: true,
      label: "Go to Cycle",
      placeholder: "Select cycle",
      options: cyclesDropdownList,
    },
    {
      name: "tab",
      type: "select",
      label: "Select Tab",
      placeholder: "Select Tab",
      options: tabOptions,
    },
    {
      name: "route",
      type: "multi-select",
      maxSelection: 1,
      closeOnSelect: true,
      label: "Select Route",
      placeholder: "Select route",
      options: routesDropdownList,
    },
    {
      name: "search",
      type: "text",
      label: "Search",
      placeholder: "Search resource id, serial number, or consumer",
    },
  ];

  const form = FormService.createForm({
    fields: navigateFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // ====== Handlers ======
  const handleFieldChange = (fieldName: string, value: any) => {
    switch (fieldName) {
      case "search":
        setSearchValue(value);
        break;

      case "route": {
        const parsedValue = value.map((v: string) => {
          try {
            return JSON.parse(v);
          } catch {
            return v;
          }
        });
        setFormState((prev) => ({ ...prev, [fieldName]: parsedValue }));
        break;
      }

      default:
        setFormState((prev) => ({ ...prev, [fieldName]: value }));
        break;
    }

    // Reset page on filter change
    setCurrentPage(1);
  };

  const handleReset = () => {
    form.reset();
    setFormState({
      cycle: [],
      tab: "",
      route: [],
      search: "",
    });
    setCurrentPage(1);
  };

  const handleNavigate = (row: any) => {
    const validationType =
      row.meterType === "4"
        ? "manual-meter-validation"
        : "photometer-validation";
    switch (formState.tab) {
      case "missing-readings":
        navigate(
          `/mx/meter-validation/${validationType}/${row.runId}?tab=missing-readings&page=1`,
        );
        setAccordionOpen("");
        return;

      default: {
        // const hasValidatorIds =
        //   Array.isArray(row?.validatorIds) && row.validatorIds.length > 0;

        // if (hasValidatorIds) {
          const params = new URLSearchParams(searchParams);
          params.set("readingId", row?.id);
          setSearchParams(params);
          setViewMode("detail");
          setValidatorId(row.validatorIds);
        // } else {
        //   toast({ title: "Validator not assigned for this reading" });
        //   setValidatorId(null);
        // }

        const currentTab =
          ruleStatusToTabMap[row.ruleStatus?.toLowerCase()] || "pending";

        navigate(
          `/mx/meter-validation/${validationType}/${row.runId}?tab=${currentTab}&page=1&readingId=${row.id}`,
        );
        setAccordionOpen("");
        return;
      }
    }
  };

  const actions: TableAction[] = [
    {
      label: "View",
      icon: <Eye size={14} />,
      onClick: (row) => handleNavigate(row),
    },
  ];

  // ====== Pagination Setup ======
  const pagination = {
    currentPage,
    totalPages: Math.ceil((pendingReadingList?.count || 0) / PAGE_SIZE),
    totalItems: pendingReadingList?.count || 0,
    itemsPerPage: PAGE_SIZE,
    startItem: (currentPage - 1) * PAGE_SIZE + 1,
    endItem: Math.min(currentPage * PAGE_SIZE, pendingReadingList?.count || 0),
  };

  const tableColumns = useMemo(() => {
    return formState.tab === "missing-readings"
      ? missingReadingHeaders
      : [
          { header: "Resource ID", key: "meterNumber" },
          { header: "Serial No", key: "deviceNumber" },
          { header: "Consumer Name", key: "consumerName" },
          {
            header: "Status",
            key: "ruleStatus",
            render: (value: string) => {
              switch (value.toLowerCase()) {
                case "new":
                  return "Pending";
                default:
                  return value;
              }
            },
          },
        ];
  }, [formState.tab]);
  // ====== Render ======
  return (
    <Accordion
      type="single"
      collapsible
      defaultValue="close"
      value={accordionOpen}
      onValueChange={setAccordionOpen}
    >
      <AccordionItem value="nav" className="border-2 rounded-lg">
        <AccordionTrigger className="px-3 py-2 text-sm font-medium">
          Navigate to Reading
        </AccordionTrigger>

        <AccordionContent className="px-3 pb-3">
          <div className="space-y-3 py-6">
            <DynamicForm
              fields={navigateFields}
              form={form}
              onSubmit={() => {}}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              config={{
                showProgress: false,
                layout: "horizontal",
                showResetButton: false,
                resetOnSubmit: false,
                classes: { buttonContainer: "hidden" },
              }}
              gridColumns={4}
            />

            <div className="border rounded-md overflow-hidden">
              <GenericTable
                columns={tableColumns}
                data={pendingReadingList?.results ?? []}
                rowKey="meterNumber"
                actions={actions}
                actionsType="icons"
                loading={isFetching}
                pagination={pagination}
                onPageChange={setCurrentPage}
                emptyMessage="No readings found"
              />
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

export default NavigateToReading;
